import { ChevronDownMedium, ChevronUpMedium } from '@zoopla-eng/zds/icons';
import { Select } from '@zoopla-eng/zds/select';
import { fontGroups } from '@zoopla-eng/zds/themes';
import { buildSelectPriceTitleForSale } from '@zoopla/redux-reducer-filters';
import { MutableRefObject, useEffect, useRef, useState } from 'react';
import { DropdownContainer } from '../DropdownContainer';
import { onDocumentClick } from '../utils/onDocumentClick';
import {
    selectPriceToggleArrow,
    selectPriceToggleButton,
    selectPriceToggleLabel,
    selectPriceToggleText,
    selectPriceWrapperStyle
} from './SelectPrice.styles.css';

const priceOptions = [
    '10000',
    '20000',
    '30000',
    '40000',
    '50000',
    '60000',
    '70000',
    '80000',
    '90000',
    '100000',
    '110000',
    '120000',
    '125000',
    '130000',
    '140000',
    '150000',
    '160000',
    '170000',
    '180000',
    '190000',
    '200000',
    '210000',
    '220000',
    '230000',
    '240000',
    '250000',
    '275000',
    '300000',
    '325000',
    '350000',
    '375000',
    '400000',
    '425000',
    '450000',
    '475000',
    '500000',
    '550000',
    '600000',
    '650000',
    '700000',
    '750000',
    '800000',
    '850000',
    '900000',
    '950000',
    '1000000',
    '1100000',
    '1200000',
    '1300000',
    '1400000',
    '1500000',
    '1600000',
    '1700000',
    '1800000',
    '1900000',
    '2000000',
    '2100000',
    '2200000',
    '2300000',
    '2400000',
    '2500000',
    '2750000',
    '3000000',
    '3250000',
    '3500000',
    '3750000',
    '4000000',
    '4250000',
    '4500000',
    '4750000',
    '5000000',
    '5500000',
    '6000000',
    '6500000',
    '7000000',
    '7500000',
    '8000000',
    '8500000',
    '9000000',
    '9500000',
    '10000000',
    '12500000',
    '15000000'
];

type prices = typeof priceOptions[number];

export type PriceMin = prices;
export type PriceMax = prices;

type SelectPriceForSaleProps = {
    value: {
        priceMin: PriceMin;
        priceMax: PriceMax;
    };
    onChange: ({
        priceMin,
        priceMax
    }: {
        priceMin: PriceMin;
        priceMax: PriceMax;
    }) => void;
};

export const SelectPriceForSale = ({
    value,
    onChange
}: SelectPriceForSaleProps) => {
    const [isActive, setIsActive] = useState(false);
    const rootNode = useRef() as MutableRefObject<HTMLDivElement>;

    const toggle = () => {
        setIsActive(!isActive);
    };

    const buttonText = buildSelectPriceTitleForSale(
        value.priceMin,
        value.priceMax
    );

    const priceMinOptions =
        value.priceMax !== ''
            ? priceOptions.slice(0, priceOptions.indexOf(value.priceMax) + 1)
            : priceOptions;

    const priceMaxOptions =
        value.priceMin !== ''
            ? priceOptions.slice(priceOptions.indexOf(value.priceMin))
            : priceOptions;

    const minPriceOptions = priceMinOptions.map((priceMinOption, index) => {
        const price = new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'GBP',
            maximumFractionDigits: 0,
            minimumFractionDigits: 0
        }).format(Number(priceMinOption));

        return {
            id: priceMinOption,
            text: price
        };
    });

    const minPriceDefaultOption = {
        id: '',
        text: 'No min'
    };

    const maxPriceDefaultOption = {
        id: '',
        text: 'No max'
    };

    const maxPriceOptions = priceMaxOptions.map((priceMaxOption, index) => {
        const price = new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'GBP',
            maximumFractionDigits: 0,
            minimumFractionDigits: 0
        }).format(Number(priceMaxOption));

        return {
            id: priceMaxOption,
            text: price
        };
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(
        onDocumentClick((evt: globalThis.MouseEvent) => {
            if (
                rootNode.current &&
                rootNode.current.contains(evt.target as Node)
            ) {
                return;
            }

            setIsActive(false);
        }),
        []
    );

    return (
        <div
            className={selectPriceWrapperStyle}
            data-testid="any_price"
            ref={rootNode}
        >
            <button
                className={`${selectPriceToggleButton} ${fontGroups.ladle}`}
                onClick={toggle}
            >
                <label className={selectPriceToggleLabel}>Price range</label>
                <span className={selectPriceToggleText}>{buttonText}</span>
                <div className={selectPriceToggleArrow} aria-hidden>
                    {isActive ? <ChevronUpMedium /> : <ChevronDownMedium />}
                </div>
            </button>
            <DropdownContainer isActive={isActive} isAlignedRight>
                <Select
                    options={[minPriceDefaultOption, ...minPriceOptions]}
                    onSelectedOptionChange={(o) => {
                        onChange({
                            ...value,
                            priceMin: o.id
                        });
                    }}
                    selectedOption={
                        minPriceOptions.find((o) => o.id === value.priceMin) ??
                        minPriceDefaultOption
                    }
                    label="Minimum price"
                />
                <Select
                    options={[maxPriceDefaultOption, ...maxPriceOptions]}
                    onSelectedOptionChange={(o) => {
                        onChange({
                            ...value,
                            priceMax: o.id
                        });
                    }}
                    selectedOption={
                        maxPriceOptions.find((o) => o.id === value.priceMax) ??
                        maxPriceDefaultOption
                    }
                    label="Maximum price"
                />
            </DropdownContainer>
        </div>
    );
};
